import { useContext } from "react";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaKommuneFilterSetting,
} from "../../../../../interfaces/filters";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";
import { f } from "../../../../../util/f";
import { kommuner } from "../../../../../util/kommune";
import { AccordionSubFilterItem } from "../../../../accordion/Accordion";
import DefaultDropdownItem from "../../../../dropdown/DefaultDropdownItem";
import SuperDropdown from "../../../../dropdown/SuperDropdown";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";

// helpers
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "kommune";

export default function FirmaKommuneFilter() {
  // context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // // find active settings
  const settings: FirmaKommuneFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // business
  const kommunerSorted = kommuner.sort((a, b) =>
    a.navn.localeCompare(b.navn, "da-DK"),
  );

  // handlers
  function updateFilterHandler(selectedItem: { kode: string; name: string }) {
    // check if selected element already is in settings otherwise add it
    if (!settings?.some((s) => s.kode === selectedItem.kode)) {
      settings.push({ kode: selectedItem.kode, name: selectedItem.name });
    }

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        settings.map((set) => {
          return {
            kommuneNr: {
              eq: parseInt(set.kode.replace("0", "")),
            },
          };
        }),
      )
      .build();

    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaKommuneFilterSetting[]
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: settings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: FirmaKommuneFilterSetting["kode"]) {
    const updatedSettings: FirmaKommuneFilterSetting[] =
      settings.filter((set) => {
        return id !== set.kode;
      }) || [];

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => {
          return {
            kommuneNr: {
              eq: parseInt(set.kode.replace("0", "")),
            },
          };
        }),
      )
      .build();

    const update = updatedSettings.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionSubFilterItem
      title="Kommune"
      value="kommune"
      isActive={!!settings.length}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.map((filter) => (
            <AppliedFilterItem
              key={filter.kode}
              label={filter.name}
              labelPrefix={filter.kode}
              removeFilterHandler={() => removeFilterHandler(filter.kode)}
            />
          ))}
        </div>
      )}

      <SuperDropdown
        items={kommunerSorted}
        itemsIdKey="kode"
        itemToString={(item) => item?.navn ?? ""}
        itemToElement={(item) => (
          <DefaultDropdownItem
            label={`${item.navn}`}
            subLabel={`Kommunekode: ${item.kode}`}
          />
        )}
        filterKeys={["navn", "kode"]}
        placeholder="Søg efter navn eller kode ..."
        clearInputAfterSelection={true}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem?.kode && item.selectedItem.navn) {
            updateFilterHandler({
              kode: item.selectedItem.kode,
              name: item.selectedItem.navn,
            });
          }
        }}
      />
    </AccordionSubFilterItem>
  );
}
